import type { StatusTone } from '@shared/design-system/ui'
import type { KycOnboarding } from '../api/verification'

export type OnboardingStatus = {
  label: 'Cadastro em andamento' | 'Em análise' | 'Correção necessária' | 'Cadastro aprovado'
  tone: StatusTone
}

export function onboardingStatus(onboarding: KycOnboarding | undefined): OnboardingStatus {
  if (!onboarding) return { label: 'Cadastro em andamento', tone: 'informative' }

  const statusByBadge: Record<KycOnboarding['badge'], OnboardingStatus> = {
    in_progress: { label: 'Cadastro em andamento', tone: 'informative' },
    in_review: { label: 'Em análise', tone: 'awaiting' },
    correction_needed: { label: 'Correção necessária', tone: 'closed' },
    approved: { label: 'Cadastro aprovado', tone: 'success' },
  }
  return statusByBadge[onboarding.badge]
}

export type OnboardingStepStatus = {
  label: 'Indisponível' | 'Preparando' | 'Pendente' | 'Enviado' | 'Corrigir' | 'Concluído'
  tone: StatusTone
}

/**
 * A step shows only the seller's part. Stripe's review spans steps, so the page shows it once;
 * a step the seller finished reads "Enviado" until the account is approved, then "Concluído".
 */
export function onboardingStepStatus(
  step:
    | KycOnboarding['steps']['profile']
    | KycOnboarding['steps']['identity']
    | KycOnboarding['steps']['bank'],
  accountApproved: boolean,
): OnboardingStepStatus {
  const sent: OnboardingStepStatus = accountApproved
    ? { label: 'Concluído', tone: 'success' }
    : { label: 'Enviado', tone: 'informative' }
  const statusByStep: Record<
    | KycOnboarding['steps']['profile']
    | KycOnboarding['steps']['identity']
    | KycOnboarding['steps']['bank'],
    OnboardingStepStatus
  > = {
    unavailable: { label: 'Indisponível', tone: 'neutral' },
    preparing: { label: 'Preparando', tone: 'informative' },
    pending: { label: 'Pendente', tone: 'neutral' },
    in_review: sent,
    fix: { label: 'Corrigir', tone: 'awaiting' },
    done: sent,
  }
  return statusByStep[step]
}

export function onboardingOperationStatus(
  operation: KycOnboarding['operations']['sales'],
  label: 'Vendas' | 'Recebimentos',
): {
  status:
    | 'Aguardando conclusão do cadastro'
    | 'Em análise'
    | 'Liberadas'
    | 'Liberados'
    | 'Bloqueadas'
    | 'Bloqueados'
  description: string
  tone: 'ok' | 'awaiting' | 'blocked' | 'neutral'
  reason?: string
} {
  if (operation === 'released') {
    return label === 'Vendas'
      ? { status: 'Liberadas', description: 'Vendas liberadas.', tone: 'ok' }
      : {
          status: 'Liberados',
          description: 'Recebimentos habilitados na conta cadastrada.',
          tone: 'ok',
        }
  }
  if (operation === 'in_review') {
    return {
      status: 'Em análise',
      description:
        label === 'Vendas'
          ? 'Liberadas após a aprovação do cadastro.'
          : 'Liberados após a aprovação do cadastro.',
      tone: 'awaiting',
    }
  }
  if (operation === 'blocked') {
    return {
      status: label === 'Vendas' ? 'Bloqueadas' : 'Bloqueados',
      description:
        label === 'Vendas'
          ? 'Bloqueadas por uma restrição operacional.'
          : 'Bloqueados por uma restrição operacional.',
      tone: 'blocked',
      reason: 'Há uma restrição operacional que impede a liberação.',
    }
  }
  return {
    status: 'Aguardando conclusão do cadastro',
    description: 'Disponíveis após a aprovação do cadastro.',
    tone: 'neutral',
  }
}

const REVIEW_TOPIC_LABELS: Readonly<Record<string, string>> = {
  personal: 'seus dados pessoais',
  company: 'dados da empresa',
  address: 'endereço',
  representative: 'dados do representante legal',
  owners: 'sócios',
  documents: 'documentos',
  identity: 'documento com foto e selfie',
  account: 'cadastro',
}

/** "dados da empresa, sócios e documentos"; unknown topics are left out. */
export function reviewingDescription(topics: readonly string[]): string | null {
  const labels = topics.flatMap((topic) => REVIEW_TOPIC_LABELS[topic] ?? [])
  if (labels.length === 0) return null
  return labels.length === 1
    ? labels[0]!
    : `${labels.slice(0, -1).join(', ')} e ${labels[labels.length - 1]}`
}
